<template>
  <div class="trials-scatter" :class="[mode]">
    <div class="overflow-wrapper">
      <div v-if="mode == 'scatter'" class="legend">
        <svg class="items" viewBox="0 0 50 50">
          <line x1="16" y1="23" x2="40" y2="23" stroke='#1e275b' stroke-width="12" stroke-opacity="0.6" />
        </svg>
        <li class="items">CI</li>
        <svg class="items" viewBox="0 0 50 50">
          <line x1="16" y1="22" x2="40" y2="22" stroke='#1e275b' stroke-width="12" stroke-opacity="0.2" />
        </svg>
        <li class="items">PI</li>
      </div>
      <svg ref="svg" :width="neededSize.width" :height="neededSize.height">
        <clipPath id="content-clip">
          <rect />
        </clipPath>
        <!-- <filter id="f3" x="0" y="0" width="200%" height="200%">
          <feOffset result="offOut" in="SourceAlpha" dx="0" dy="2" />
          <feColorMatrix result="matrixOut" in="offOut" type="matrix"
            values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 0.4 0" />
          <feGaussianBlur result="blurOut" in="matrixOut" stdDeviation="3" />
          <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
        </filter>-->
      </svg>
      <div class="brush-notice" v-if="brushMode">
        {{
    brushMode === "rect"
      ? $t("brush_notice_rect")
      : $t("brush_notice_poly")
  }}
      </div>
      <div class="zoom-reset" v-if="zoomed" @click="resetZoom">
        {{ $t("reset_zoom") }}</div>
      <scatter-tooltip ref="tooltip" :data="spiderRoot" v-if="spiderRoot"></scatter-tooltip>
    </div>
    <vue-draggable-resizable ref="tippyLayer" class="tippy-layer" :class="{ dragged: draggedTooltip }"
      drag-handle=".tippy-box" @dragging="onTooltipDragging"></vue-draggable-resizable>
  </div>
</template>

<script>
import d3 from "@/utils/d3importer";
import widgetContent from "@/mixins/widgetContent";
import { padLinear } from "@/utils";
import polygonBrush from "@/utils/PolygonBrush";
import rectBrush from "@/utils/RectBrush";
import ScatterTooltip from "@/components/commons/ScatterTooltip";
import { getNestedAttr } from "@/utils";
// import {createPopper} from '@popperjs/core';
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css"; // optional for styling
import VueDraggableResizable from "vue-draggable-resizable";
import "@/styles/components/VueDraggableResizable.css";
import * as ss from 'simple-statistics'

export default {
  name: "TrialsScatter",
  props: [
    "data",
    "colorTag",
    "symbolTag",
    "colorGroups",
    "xAttr",
    "yAttr",
    "alphaAttr",
    "sizeAttr",
    "sortAttr",
    "mode",
    "color",
    "brushMode",
    "selection",
    "defaultSize",
    "highlightedSubject",
    "generatingReport", // used to change syle for report generation
    "showDQTLine"
  ],
  components: { ScatterTooltip, VueDraggableResizable },
  mixins: [widgetContent],
  model: {
    prop: "highlightedSubject",
    event: "change",
  },
  data() {
    return {
      spiderRoot: null,
      // highlightedData: null,
      layoutX: null,
      layoutY: null,
      zoomTransform: null,
      draggedTooltip: false,
    };
  },
  mounted() {
    this.resetChart();
  },
  beforeDestroy() {
    // this.highlightedData = null
    if (this.lastTippy) {
      this.lastTippy.destroy();
    }
  },
  computed: {
    neededSize() {
      return {
        width: Math.max(
          this.size.width,
          (this.layoutX && this.layoutX.width) || 0
        ),
        height: Math.max(
          this.size.height,
          (this.layoutY && this.layoutY.height) || 0
        ),
      };
    },
    zoomed() {
      let content = d3.select(this.$refs.svg).selectAll(".content").data([null]);
      content.selectAll('.point').raise()
      return (
        this.zoomTransform !== null &&
        (this.zoomTransform.k !== 1 ||
          Math.abs(this.zoomTransform.x) > 1 ||
          Math.abs(this.zoomTransform.y) > 1)
      );
    },
  },
  methods: {
    attrGetter(d, attr) {
      let val = getNestedAttr(d, attr).value || getNestedAttr(d, attr);
      if (isNaN(+val)) return val;
      else return +val;
    },
    getUnit(attr) {
      let ob = getNestedAttr(this.data[0], attr);
      return ob.unit || null;
    },
    xGetter(d) {
      return +this.attrGetter(d, this.xAttr) || 0;
    },
    sortGetter(d) {
      return this.attrGetter(d, this.sortAttr);
    },
    yGetter(d) {
      return +this.attrGetter(d, this.yAttr) || 0;
    },
    sizeGetter(d) {
      if (this.sizeAttr) return this.attrGetter(d, this.sizeAttr);
      else return 1;
    },
    alphaGetter(d) {
      if (this.alphaAttr) return this.attrGetter(d, this.alphaAttr);
      else return 1;
    },
    drawChart() {
      let self = this;
      let data = this.data || [];
      let svg = d3.select(this.$refs.svg);

      this.makePatterns();

      // this.highlightedData = this.selection
      let content = svg.selectAll(".content").data([null]);

      let tooltipContainer = d3.select(this.$refs.tooltipContainer);
      tooltipContainer.style(
        "transform",
        `translate(${this.margin.left}px,${this.margin.top}px)`
      );

      let contentEnter = content
        .enter()
        .append("g")
        .attr("class", "content")
        .attr("clip-path", "url(#content-clip)");
      contentEnter
        .append("rect")
        .attr("class", "background")
        .on("click", this.select);
      content = content.merge(contentEnter);
      content.attr(
        "transform",
        `translate(${this.margin.left},${this.margin.top})`
      );
      content
        .select(".background")
        .attr(
          "width",
          this.neededSize.width - this.margin.left - this.margin.right
        )
        .attr(
          "height",
          this.neededSize.height - this.margin.top - this.margin.bottom
        )
        .style('fill', this.generatingReport ? 'rgba(255, 255, 255, 0.55)' : 'rgba(255, 255, 255, 0.15)')
      // .attr('width',self.size.width-this.margin.left-this.margin.right).attr('height',self.size.height-this.margin.top-this.margin.bottom)
      if (this.mode === "scatter" && this.xAttr != "subject.templateId") {
        content.call(
          d3
            .zoom()
            .scaleExtent([1, 20])
            .translateExtent([
              [0, 0],
              [self.size.width, self.size.height],
            ])
            .on("zoom", () => {
              if (!this.axes) return;
              if (this.x.invert) {
                this.axes.x.scale(d3.event.transform.rescaleX(this.x));
                this.axes.y.scale(d3.event.transform.rescaleY(this.y));
              } else {
                this.axes.x.scale(
                  this.x
                    .copy()
                    .range(
                      [
                        this.margin.left,
                        this.size.width - this.margin.right,
                      ].map((d) => d3.event.transform.applyX(d))
                    )
                );
              }
              this.updateAxes();
              // translate linear regression points
              if (this.xAttr != 'subject.templateId') { this.updateRegressions() }
              this.drawSpider(this.spiderRoot, { transition: false });
              //Esto rasca "bastante" con mas de 500 puntos...
              let points = content.selectAll(".point");

              points.attr(
                "transform",
                (d) => `translate(${this.layoutX(d)},${this.layoutY(d)})`
              );
              this.zoomTransform = d3.event.transform;
            })
        );
      } else {
        content.selectAll(".regression").remove();
        content.selectAll(".confidence-interval").remove();
        content.selectAll(".prediction-interval").remove();
        content.selectAll('.cmax-line').remove()
        content.on(".zoom", null);
      }
      content.selectAll('.point').raise()
    },
    resetZoom() {
      // d3.select(this.$refs.svg).selectAll('.content').call(d3.zoom().transform, d3.zoomIdentity);
      // this.resetAxes()
      this.resetChart();
      this.zoomTransform = null;
    },
    drawDQTLine() {
      let content = d3.select(this.$refs.svg).selectAll(".content");
      let dqtThreshold = 10
      let xValues = this.data.map((d) => this.xGetter(d));
      let maxDose = d3.max(xValues)
      content.append('line').attr('class', 'cmax-line')
        .attr("x1", this.axes.x.scale()(0))
        .attr("x2", this.axes.x.scale()(maxDose))
        .attr("y1", this.axes.y.scale()(dqtThreshold))
        .attr("y2", this.axes.y.scale()(dqtThreshold))
        .attr('stroke-dasharray', "4,4")
        .attr('stroke', '#ff0000')
        .attr('stroke-width', 1);
    },
    updateRegressions() {
      let content = d3.select(this.$refs.svg).selectAll(".content").data([null])
      if (this.data != null && this.mode == "scatter" && this.xAttr != 'subject.templateId') {
        content.selectAll(".regression").remove();
        content.selectAll('.confidence-interval').remove()
        content.selectAll('.prediction-interval').remove()
        for (let i = 0; i < this.colorGroups.length; i++) {
          if (this.colorGroups[i].paintRegression) {
            this.drawLinearRegression(this.colorGroups[i])
          }
        }
      }
      content.selectAll('.point').raise()
    },
    updatePoints() {
      let data = this.data || [];
      let x, y, size, alpha, w, h;
      if (this.data != null && this.mode == "scatter" && this.xAttr != 'subject.templateId') {
        
        let content = d3.select(this.$refs.svg).selectAll(".content").data([null])
        this.computeRegressions()
        content.selectAll(".regression").remove();
        content.selectAll('.confidence-interval').remove()
        content.selectAll('.prediction-interval').remove()
        content.selectAll('.cmax-line').remove()
        for (let i = 0; i < this.colorGroups.length; i++) {
          if (this.colorGroups[i].paintRegression) {
            this.drawLinearRegression(this.colorGroups[i])
          }
        }
        if (this.showDQTLine) this.drawDQTLine()
        else content.selectAll('.cmax-line').remove()
      }
      if (this.sizeAttr)
        size = d3
          .scaleLinear()
          .domain(d3.extent(data, (d) => this.sizeGetter(d)))
          .range([10, 180]);
      else size = (d) => this.defaultSize;
      if (this.alphaAttr)
        alpha = d3
          .scaleLinear()
          .domain(d3.extent(data, (d) => this.alphaGetter(d)))
          .range([0.3, 1]);
      else alpha = (d) => 1;

      let content = d3.select(this.$refs.svg).selectAll(".content");

      let points = content
        .selectAll(".point")
        .data(data, (d, i) => d.trialId + "_" + d.subject.id);

      let pointsEnter = points.enter().append("g").attr("class", "point");
      pointsEnter.append("path").attr("class", "dot");
      pointsEnter
        .append("path")
        .attr("class", "ghost")
        .attr("fill", "transparent"); //.attr('fill',(d)=>'rgba(255,0,0,0.3)')
      pointsEnter
        .attr(
          "transform",
          (d) => `translate(${this.layoutX(d)},${this.layoutY(d)})`
        )
        .on("click", this.select);

      points.exit().remove();

      points = points.merge(pointsEnter); //.attr('r',3))

      // let circlePath = (r)=>{
      //   return `M ${-r} 0 a ${r} ${r} 0 1 0 ${2*r} 0 a ${r} ${r} 0 1 0 ${-2*r} 0`
      // }
      // let rectPath = (r)=>{
      //   let w = 2*r
      //   return `M ${-w/2},${-w/2} h ${w} v ${w} h ${-w} v ${-w}`
      // }
      points
        .classed(
          "selected",
          (d) =>
            this.selection &&
            this.selection.findIndex((dd) => dd.subject.id === d.subject.id) >=
            0
        )
        .classed(
          "unselected",
          (d) =>
            this.selection &&
            this.selection.findIndex((dd) => dd.subject.id === d.subject.id) < 0
        );
      points
        .transition()
        .duration(this.generatingReport ? 0 : 600)
        .attr(
          "transform",
          (d) => `translate(${this.layoutX(d)},${this.layoutY(d)})`
        );

      let symbol = d3
        .symbol()
        .type((d) => {
          if (this.mode === "heatmap") {
            return d3.symbolSquare;
          } else {
            if (d.tagSymbol == 'circle') return d3.symbolCircle
            else if (d.tagSymbol == 'cross') return d3.symbolCross
            else if (d.tagSymbol == 'square') return d3.symbolSquare
            else if (d.tagSymbol == 'triangle') return d3.symbolTriangle
            else if (d.tagSymbol == 'diamond') return d3.symbolDiamond
            else if (d.tagSymbol == 'wye') return d3.symbolWye
            else if (d.tagSymbol == 'star') return d3.symbolStar
            /* let isMale = d["subject"]["general_characteristics"].filter(item => item.view.name === "Is Male")[0];
            if (isMale.value[0]) {
              return d3.symbolCircle;
            } else {
              return d3.symbolCross;
            } */
          }
        })
        .size((d) => {
          if (this.mode === "heatmap") {
            // return Math.pow(2*size(this.sizeGetter(d)),2)
            return 15 * size(this.sizeGetter(d));
          } else {
            // return Math.pow(size(this.sizeGetter(d)),2)
            return size(this.sizeGetter(d)) * 2;
          }
        });
      let ghostSymbol = d3
        .symbol()
        .type((d) => {
          if (this.mode === "heatmap") {
            return d3.symbolSquare;
          } else {
            if (d.tagSymbol == 'circle') return d3.symbolCircle
            else if (d.tagSymbol == 'cross') return d3.symbolCross
            else if (d.tagSymbol == 'square') return d3.symbolSquare
            else if (d.tagSymbol == 'triangle') return d3.symbolTriangle
            else if (d.tagSymbol == 'diamond') return d3.symbolDiamond
            else if (d.tagSymbol == 'wye') return d3.symbolWye
            else if (d.tagSymbol == 'star') return d3.symbolStar
            /* let isMale = d["subject"]["general_characteristics"].filter(item => item.view.name === "Is Male")[0];
            if (isMale.value[0]) {
              return d3.symbolCircle;
            } else {
              return d3.symbolCross;
            } */
          }
        })
        .size((d) => {
          if (this.mode === "heatmap") {
            // return Math.pow(2*size(this.sizeGetter(d)),2)
            return 17 * size(this.sizeGetter(d));
          } else {
            // return Math.pow(size(this.sizeGetter(d)),2)
            return 3 * size(this.sizeGetter(d));
          }
        });
      points
        .select(".dot")
        //.transition()
        //.duration(300)
        .attr("fill", (d) => {
          if (this.mode == "scatter") return d.isImported ? d.tagColor : '#FFF'
          else return this.color(d)
        })
        .attr("fill-opacity", (d) => d.isImported ? 0.6 : 1)
        .attr("stroke", (d) => this.mode == "scatter" ? d.tagColor : this.color(d))
        .attr("stroke-width", this.generatingReport ? "2" : "3")
        .attr("d", (d) => {
          return symbol(d);
        })
        .attr("opacity", (d) => alpha(this.alphaGetter(d)));
      points
        .select(".ghost")
        // .attr('r',(d)=>Math.sqrt(size(this.sizeGetter(d))))
        .attr("d", (d) => {
          return ghostSymbol(d);
        });
      content.selectAll('.point').raise()
    },
    updateSelecteds() {
      let points = d3
        .select(this.$refs.svg)
        .select(".content")
        .selectAll(".point");
      let highlightIds;
      if (this.clickedSelection) {
        highlightIds = this.getSiblings(this.clickedSelection).map(
          (d) => d.subject.id
        );
      } else if (this.highlightedSubject) {
        highlightIds = [this.highlightedSubject.subject.id];
      }
      points
        .classed(
          "selected",
          (d) =>
            this.selection &&
            this.selection.findIndex((dd) => dd.subject.id === d.subject.id) >=
            0
        )
        .classed(
          "unselected",
          (d) =>
            this.selection &&
            this.selection.findIndex((dd) => dd.subject.id === d.subject.id) < 0
        )
        .classed(
          "highlighted",
          (dd) => highlightIds && highlightIds.indexOf(dd.subject.id) >= 0
        )
        .classed(
          "unhighlighted",
          (dd) => highlightIds && highlightIds.indexOf(dd.subject.id) < 0
        );

    },
    updateScales() {
      let data = this.data || [];
      let w, h, x, y;
      if (this.mode === "scatter") {
        this.margin = { top: 0, left: 40, right: 0, bottom: 40 };
        if (this.xAttr === "subject.templateId") this.margin.bottom = 60;
        w = this.size.width - this.margin.left - this.margin.right;
        h = this.size.height - this.margin.top - this.margin.bottom;
        if (this.xAttr === "subject.templateId") {
          let domainValues = data.map((d) => this.xGetter(d));
          domainValues = domainValues.filter(
            (v, i) => i === domainValues.indexOf(v)
          );
          x = d3.scaleBand().domain(domainValues).range([0, w]).padding(0.1);
        } else {
          x = d3
            .scaleLinear()
            .domain(
              padLinear(
                d3.extent(data, (d) => this.xGetter(d)),
                0.05
              )
            )
            .range([0, w])
            .nice();
        }
        y = d3
          .scaleLinear()
          .domain(
            padLinear(
              d3.extent(data, (d) => this.yGetter(d)),
              0.05
            )
          )
          .range([h, 0])
          .nice();
      } else {
        this.margin = { top: 3, left: 3, right: 3, bottom: 3 };
        w = this.size.width - this.margin.left - this.margin.right;
        h = this.size.height - this.margin.top - this.margin.bottom;
        x = d3
          .scaleLinear()
          .domain(d3.extent(data, (d) => this.xGetter(d)))
          .range([0, w]);
        y = d3
          .scaleLinear()
          .domain(d3.extent(data, (d) => this.yGetter(d)))
          .range([h, 0]);
      }

      this.x = x;
      this.y = y;
    },
    computeRegressions() {
      for (let i = 0; i < this.colorGroups.length; i++) {
        if (this.colorGroups[i].name !== 'Not provided') {
          let colorGroupData = this.colorGroups[i].subjects.map((d) => [this.xGetter(d), this.yGetter(d)]);
          colorGroupData = colorGroupData.sort((a, b) => {
            if (a[0] > b[0]) return 1;
            if (a[0] < b[0]) return -1;
            else return 0;
          });
          this.colorGroups.splice(i, 1,
            this.colorGroups[i] = {
              ...this.colorGroups[i],
              regressionData: this.calculateLinearRegression(colorGroupData)
            }
          )
        }
      }
      this.$emit('update', this.colorGroups);
    },
    // Computes linear regression and regression variables from data points
    calculateLinearRegression(data) {
      let linearRegressionValues = ss.linearRegression(data);
      let linearRegressionLine = ss.linearRegressionLine(linearRegressionValues);

      let firstX = data[0];
      let lastX = data.slice(-1)[0];

      let xCoordinates = [firstX, lastX];
      let linearRegression = xCoordinates.map(d => [d[0], linearRegressionLine(d[0])])

      /// compute regression variables
      let yAxis = data.map(item => item[1]);
      let xAxis = data.map(item => item[0]);
      let rPrediction = [];

      let meanValue = 0; // MEAN VALUE
      let TSS = 0; // THE TOTAL SUM OF THE SQUARES
      let RSS = 0; // RESIDUAL SUM OF SQUARES
      let sum = 0;
      let sumX = 0
      let lower = 0;
      for (let n in yAxis) {
        sum += yAxis[n];
        sumX += xAxis[n];
      }
      meanValue = (sum / yAxis.length);
      let meanX = (sumX / xAxis.length)

      // Calculate based on datapoints and the regression function predictions
      for (let i in xAxis) {
        TSS += Math.pow(yAxis[i] - meanValue, 2);
        rPrediction.push(xAxis[i] * linearRegressionValues.m + linearRegressionValues.b); // Compute prediction for every xValue
        RSS += Math.pow(yAxis[i] - rPrediction[i], 2);
        lower += Math.pow(xAxis[i] - meanX, 2)
      }
      let MSE = RSS / xAxis.length
      let rSquared = 1 - (RSS / TSS);

      /// Compute confidence and prediction intervals (compute interval for every x)
      let confidenceData = []
      let predictionData = []
      for (let i in xAxis) {
        confidenceData.push(this.confidenceInterval(xAxis[i], linearRegressionValues.m, linearRegressionValues.b, RSS, meanX, lower, xAxis))
        predictionData.push(this.predictionInterval(xAxis[i], linearRegressionValues.m, linearRegressionValues.b, MSE, lower, xAxis, meanX))
      }
      return {
        'linearRegression': linearRegression,
        'm': linearRegressionValues.m.toFixed(6),
        'n': linearRegressionValues.b.toFixed(3),
        'r2': rSquared.toFixed(2),
        'confData': confidenceData,
        'predData': predictionData,
      };
    },
    confidenceInterval(x, m, n, RSS, meanX, lower, xAxis) {
      /// confidence interval around the regression line (m,n) for a given (x)
      let Y = x * m + n
      let sy = Math.sqrt(RSS / (xAxis.length / 2))
      let t = 1.96
      let SE = sy * Math.sqrt(1 / xAxis.length + Math.pow(x - meanX, 2) / lower)
      return { 'x': x, 'left': Y - t * SE, 'right': Y + t * SE }
    },
    predictionInterval(x, m, n, MSE, lower, xAxis, meanX) {
      /// prediction interval around the regression line (m,n) for a given (x)
      let Y = x * m + n
      let t = 1.96
      let var1 = (1 + 1 / (xAxis.length) * Math.pow(x - meanX, 2) / lower)
      let var2 = Math.sqrt(MSE * var1)
      return { 'x': x, 'left': Y - t * var2, 'right': Y + t * var2 }
    },
    drawLinearRegression(tagGroupData) {
      let content = d3.select(this.$refs.svg).selectAll(".content").data([null])

      // DRAW INTERVALS
      let xAxis = tagGroupData.subjects.map(item => item[0]);

      // Prediction interval (from computed interval for every x)
      let mappedValues1 = []
      for (let i in xAxis) {
        mappedValues1.push({
          "x": this.axes.x.scale()(tagGroupData.regressionData.predData[i].x),
          "left": this.axes.y.scale()(tagGroupData.regressionData.predData[i].left),
          "right": this.axes.y.scale()(tagGroupData.regressionData.predData[i].right)
        })
      }
      let areaGenerator1 = d3.area()
        .x((d) => d.x)
        .y0((d) => d.left)
        .y1((d) => d.right)
      content.append("path").datum(mappedValues1)
        .attr("class", "prediction-interval").attr("id", tagGroupData.key)
        .attr("fill", tagGroupData.color)
        .attr("d", areaGenerator1)
        .style("opacity", "0.1")

      // Confidence inter (from computed interval for every x)
      let mappedValues = []
      for (let i in xAxis) {
        mappedValues.push({
          "x": this.axes.x.scale()(tagGroupData.regressionData.confData[i].x),
          "left": this.axes.y.scale()(tagGroupData.regressionData.confData[i].left),
          "right": this.axes.y.scale()(tagGroupData.regressionData.confData[i].right)
        })
      }
      let areaGenerator = d3.area()
        .x((d) => d.x)
        .y0((d) => d.left)
        .y1((d) => d.right)
      content.append("path").datum(mappedValues)
        .attr("class", "confidence-interval").attr("id", tagGroupData.key)
        .attr("fill", tagGroupData.color)
        .attr("d", areaGenerator)
        .style("opacity", "0.2")

      // DRAW REGRESSION
      if (tagGroupData.isImported) {
        content.append("path").attr("class", "regression").attr("id", `regression-${String(tagGroupData.key)}`).attr("stroke-dasharray", "5.5").attr("stroke", "black").attr("stroke-width", 1.5)
      } else {
        content.append("path").attr("class", "regression").attr("id", `regression-${String(tagGroupData.key)}`).attr("stroke", "black").attr("stroke-width", 1.5)
      }

      // DRAW REGRESSION TOOLTIP
      let g = content.append("g")
      g.append("circle").style("opacity", 0).attr("class", "regression-tooltip").attr('r', 5).style('fill', 'grey');
      let x = this.x
      let y = this.y
      let mouseover = function (d) {
        content.selectAll(".regression-tooltip")
          .attr("cx", (d3.mouse(this)[0]) + "px")
          .attr("cy", (d3.mouse(this)[1]) + "px")
        // calculate hovered point by inverting scale on mouseover
        let xVal = x.invert(d3.mouse(this)[0]).toFixed(2)
        let yVal = y.invert(d3.mouse(this)[1]).toFixed(2)
        this.regressionTooltip = tippy('.regression-tooltip', {
          content: `${xVal}, ${yVal}`,
          theme: 'light'
        })
      }
      let mousemove = function () {
      }
      let mouseleave = function (d) {
      }
      let line = d3.select("#regression-" + tagGroupData.key)
        .on("mouseover", mouseover)
        .on("mousemove", mousemove)
        .on("mouseleave", mouseleave)
      let plotCoords = tagGroupData.regressionData.linearRegression.map(item => [this.axes.x.scale()(item[0]), this.axes.y.scale()(item[1])]);
      line.attr("d", d3.line()(plotCoords));
    },
    resetAxes() {
      let data = this.data || [];
      let svg = d3.select(this.$refs.svg);
      let axesUpdate = svg.selectAll(".axis").data(["x", "y"]);
      let axesEnter = axesUpdate
        .enter()
        .append("g")
        .attr("class", (d) => "axis " + d + "-axis")
        .lower();
      axesUpdate = axesUpdate.merge(axesEnter);

      let x = this.x;
      let y = this.y;

      let axes;
      if (this.mode === "scatter") {
        axes = {
          x: d3.axisBottom().scale(x).ticks(10),
          y: d3.axisLeft().scale(y).ticks(5),
        };

        this.layoutX = (d) => {
          return (
            axes.x.scale()(this.xGetter(d)) +
            (axes.x.scale().bandwidth ? axes.x.scale().bandwidth() / 2 : 0)
          );
        };
        this.layoutY = (d) => axes.y.scale()(this.yGetter(d));
      } else {
        svg.selectAll(".axis").data([]).exit().remove();
        let sortedData = [...data].sort(
          (a, b) => this.sortGetter(b) - this.sortGetter(a)
        );
        let w = this.size.width - this.margin.left - this.margin.right;
        let h = this.size.height - this.margin.top - this.margin.bottom;
        let separation = 24;
        let cols = Math.floor(w / separation);
        let mrg = (w - cols * separation) / 2;

        let layoutWidth = cols * separation + 2 * mrg;
        let layoutHeight =
          Math.ceil(sortedData.length / cols) * separation + 2 * mrg;

        if (layoutHeight > h) {
          w = this.size.width - this.margin.left - this.margin.right - 20;
          h = this.size.height - this.margin.top - this.margin.bottom;
          separation = 24;
          cols = Math.floor(w / separation);
          mrg = (w - cols * separation) / 2;
          layoutWidth = cols * separation + 2 * mrg;
          layoutHeight =
            Math.ceil(sortedData.length / cols) * separation + 2 * mrg;
        }
        this.layoutX = (d) => {
          return (
            (sortedData.indexOf(d) % cols) * separation + separation / 2 + mrg
          );
        };
        this.layoutY = (d) => {
          return (
            Math.floor(sortedData.indexOf(d) / cols) * separation +
            separation / 2 +
            mrg
          );
        };
        this.layoutX.width = layoutWidth;
        this.layoutY.height = layoutHeight;
      }

      this.axes = axes;
      let content = svg.selectAll(".content").data([null]);
      content.call(d3.zoom().transform, d3.zoomIdentity);
    },
    updateAxes() {
      if (!this.data) return;
      let self = this;
      let axesSelection = d3.select(this.$refs.svg).selectAll(".axis");
      let margin = this.margin;
      let axes = this.axes;
      let size = this.size;
      axesSelection.each(function (d) {
        let axis = d3.select(this);
        axis.call(axes[d]).attr("transform", () => {
          if (d === "y") return `translate(${margin.left},${margin.top})`;
          else
            return `translate(${margin.left},${size.height - margin.bottom})`;
        });
        axis
          .selectAll(".tick")
          .select("line")
          .attr("x1", d === "y" ? -margin.left : 0)
          .attr("x2", d === "y" ? size.width : 0)
          .attr("y1", d === "x" ? -size.height : 0)
          .attr("y2", d === "x" ? margin.bottom : 0)
          .style("pointer-events", "none")
          .style("stroke", self.generatingReport ? 'rgba(0, 0, 0, 1)' : 'white')
          .style("stroke-width", self.generatingReport ? 0.5 : 1)
        axis
          .selectAll(".tick")
          .select("text")
          .attr("text-anchor", "start")
          .attr("dy", d === "x" ? "0.7em" : "-0.7em")
          .attr("dx", d === "y" ? "-1.4em" : "0.4em");
        if (d === "x")
          axis
            .selectAll(".domain")
            .attr("d", `M ${-margin.left} 0 h ${size.width + margin.right}`);

        if (d === "x" && self.xAttr === "subject.templateId") {
          let tickSpace = size.width / axis.selectAll(".tick").size();
          if (tickSpace < 10)
            axis.selectAll(".tick").select("text").style("display", "none");
          else
            axis
              .selectAll(".tick")
              .select("text")
              .style("display", "inherit")
              .attr("transform", "rotate(-90)")
              .attr("text-anchor", "end")
              .attr("dy", "-1.1em")
              .attr("dx", "-0.4em");
        }

        let labels = axis.selectAll(".label").data([null]);
        let labelsEnter = labels
          .enter()
          .append("text")
          .attr("class", "label")
          .attr("fill", "#000")
          .style("font-size", this.generatingReport ? "16px" : "12px")
          .style("font-weight", "bold")
          .style("opacity", this.generatingReport ? "1" : "0.6");
        labels
          .merge(labelsEnter)
          .text(() => {
            if (!self[d + "Attr"]) return "";
            let attr = self[d + "Attr"].split(".").slice(-1)[0];
            let unit = self.getUnit(self[d + "Attr"]);
            if (attr === "templateId") attr = "Subject (categorical)";
            return `${attr}` + (unit ? ` (${unit})` : "");
          })
          .attr("transform", (dd) => {
            if (d === "x")
              return `translate(${size.width - self.margin.left - 5},-5)`;
            if (d === "y") return `translate(5,${self.margin.top + 5})`;
          })
          .attr("dominant-baseline", () =>
            d === "x" ? "text-after-edge" : "hanging"
          )
          .attr("text-anchor", () => (d === "x" ? "end" : "start"))
          .raise();
      });

      let clippath = d3.select(this.$refs.svg).select("#content-clip");
      clippath
        .select("rect")
        .attr(
          "width",
          this.neededSize.width - this.margin.left - this.margin.right
        )
        .attr(
          "height",
          this.neededSize.height - this.margin.top - this.margin.bottom
        );
    },
    updateBrush() {
      let svg = d3.select(this.$refs.svg);
      let brushFunc;
      if (this.brushMode === "rect") brushFunc = rectBrush();
      else if (this.brushMode === "poly") brushFunc = polygonBrush();

      let brush = svg
        .selectAll(".brush")
        .data([this.brushMode + this.mode], (d) => d);
      brush.exit().remove();
      let brushEnter = brush
        .enter()
        .append("g")
        .attr("class", "brush " + this.brushMode);
      if (brushFunc) {
        brushFunc
          .x(this.x)
          .y(this.y)
          .on("end", () => {
            let newSelection = this.data.filter((d) =>
              brushFunc.isWithinExtent(this.layoutX(d), this.layoutY(d))
            );
            this.$emit("select", newSelection.length > 0 ? newSelection : null);
            setTimeout(() => this.$emit("brush"), 200);
          })
          .on("brush", () => {
            let newSelection = this.data.filter((d) =>
              brushFunc.isWithinExtent(this.layoutX(d), this.layoutY(d))
            );
            this.$emit("select", newSelection.length > 0 ? newSelection : null);
          });

        brush
          .merge(brushEnter)
          .call(brushFunc)
          .attr(
            "transform",
            `translate(${this.margin.left},${this.margin.top})`
          );
      }
    },
    // hover(d){
    //   this.drawSpider(d)
    // },
    select(d) {
      // if(!d) return //??? a ver si cuela algo asi...
      if (!d || d === this.clickedSelection) this.clickedSelection = null;
      else this.clickedSelection = d;

      if (this.brushMode) return;

      this.spiderRoot = this.clickedSelection;
      this.$emit("change", this.clickedSelection);
      this.$nextTick(this.placeTooltip);
      this.drawSpider(this.clickedSelection);
      // if(!this.selection) this.$emit('select',d?this.getSiblings(d):null)
    },
    placeTooltip() {
      // ESCUCHAR AL TIPPY PARA CARGARSE LA LINEA DE PUNTITOS!?!?
      if (this.lastTippy) {
        this.lastTippy.destroy();
      }
      if (!this.$refs.tooltip) return;
      const target = d3
        .select(this.$refs.svg)
        .select(".content")
        .selectAll(".point")
        .filter((d) => d === this.spiderRoot)
        .node();
      const tooltip = this.$refs.tooltip.$el;
      this.tooltipTarget = target;
      this.tooltipEl = tooltip;
      let self = this;
      this.lastTippy = tippy(target, {
        content: tooltip,
        showOnCreate: true,
        trigger: "click",
        // customClass: 'tooltip-custom',
        maxWidth: 340,
        interactive: true,
        appendTo: this.$refs.tippyLayer.$el,
        // interactiveDebounce: 75,
        onHide: () => {
          // console.log("tippy gone, should hide line");
          self.draggedTooltip = false;
          let content = d3.select(self.$refs.svg).select(".content");
          let draggedLine = content.selectAll(".dragged-line").data([]);
          draggedLine.exit().remove();
        },
      });
    },
    getSiblings(d) {
      return this.data.filter(
        (dd) => d && dd.subject.templateId === d.subject.templateId
      );
    },
    drawSpider(d, options) {
      if (!this.data) return;
      if (!options) options = {};

      d3.select(this.$refs.svg)
        .select(".content")
        .selectAll(".dragged-line")
        .data([])
        .exit()
        .remove();
      this.draggedTooltip = false;

      let spiderData = d ? [d] : [];
      let spider = d3
        .select(this.$refs.svg)
        .select(".content")
        .selectAll(".spider")
        .data(spiderData, (d) => d.subject.id);
      spider.exit().transition().style("opacity", 0).remove();
      let spiderEnter = spider
        .enter()
        .append("g")
        .attr("class", "spider")
        .style("opacity", 0);
      spider = spider.merge(spiderEnter);
      spider.lower();
      spider.transition().style("opacity", 1);

      let siblings = this.getSiblings(d);
      let legs = spider
        .selectAll(".leg")
        .data(siblings, (dd) => d.subject.id + "_" + dd.subject.id);
      let legsEnter = legs.enter().append("line").attr("class", "leg");
      legsEnter
        .attr("x1", (dd) => this.layoutX(d))
        .attr("x2", (dd) => this.layoutX(dd))
        .attr("y1", () => this.layoutY(d))
        .attr("y2", (dd) => this.layoutY(dd));
      legs = legs.merge(legsEnter);
      if (options.transition !== false) legs = legs.transition();
      legs
        .attr("x1", (dd) => this.layoutX(d))
        .attr("x2", (dd) => this.layoutX(dd))
        .attr("y1", () => this.layoutY(d))
        .attr("y2", (dd) => this.layoutY(dd));

      // let labels = spider.selectAll('.label').data(siblings)
      // let labelsEnter = labels.enter().append('g').attr('class','label')
      // labelsEnter.append('rect').attr('width',100).attr('height',25).attr('rx',13)
      //   .attr('transform','translate(-50,-15)').attr('fill',(dd)=>this.color(dd)).attr('stroke','#fff')
      // labelsEnter.append('text').attr('fill','#fff').attr('text-anchor','middle').text((dd)=>dd.subject.templateId)
      // labels = labels.merge(labelsEnter)
      // labels.attr('transform',(dd)=>`translate(${this.layoutX(dd)},${this.layoutY(dd)+30})`)

      let points = d3
        .select(this.$refs.svg)
        .select(".content")
        .selectAll(".point");
      // COULD BRING BACK
      // points
      //   .classed("highlighted",(dd) => d && siblings.indexOf(dd)>=0)
      //   .classed("unhighlighted", (dd) => d && siblings.indexOf(dd)<0)
      points.each(function (dd) {
        if (siblings.indexOf(dd) >= 0) d3.select(this).raise();
      });
    },
    makePatterns() {
      let svg = d3.select(this.$refs.svg);
      let defs = svg.selectAll("defs").data([null]);
      defs
        .enter()
        .append("defs")
        .append("pattern")
        .attr("id", "diagonalHatch")
        .attr("patternUnits", "userSpaceOnUse")
        .attr("width", 4)
        .attr("height", 4)
        .append("path")
        .attr("fill", "red")
        .attr("d", "M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2")
        .attr("stroke", "#ccc")
        .attr("stroke-width", 1);
    },
    resetChart() {
      this.updateScales();
      this.resetAxes();
      this.drawChart();
      this.updateAxes();
      this.updatePoints();
      this.drawSpider(this.spiderRoot);
    },
    onTooltipDragging() {
      this.draggedTooltip = true;
      let content = d3.select(this.$refs.svg).select(".content");
      let draggedLine = content.selectAll(".dragged-line").data([null]);
      draggedLine = draggedLine.merge(
        draggedLine
          .enter()
          .append("line")
          .attr("class", "dragged-line")
          .attr("stroke", "#3D3F50aa")
          .attr("stroke-width", "3")
          .attr("stroke-dasharray", "6 6")
      );
      let tr = this.tooltipTarget.transform.baseVal.consolidate().matrix;
      let sp = this.tooltipEl.getBoundingClientRect();
      draggedLine
        .attr("x2", sp.left + sp.width / 2)
        .attr("y2", sp.top - sp.height / 2)
        .attr("x1", tr.e)
        .attr("y1", tr.f);
    },
    // onTooltipDragStop() {
    //   // console.log("dragging tootlip no mo");
    //   // this.draggedTooltip = false
    // },
  },
  watch: {
    data() {
      this.resetChart();
    },
    colorTag() {
      this.resetChart();
    },
    colorGroups() {
      this.updateRegressions();
    },
    symbolTag() {
      this.resetChart();
    },
    brushMode() {
      // if(this.brushMode) this.selection = null
      this.updateBrush();
    },
    xAttr() {
      this.resetChart();
    },
    yAttr() {
      this.resetChart();
    },
    sortAttr() {
      this.resetChart();
    },
    alphaAttr() {
      this.updatePoints();
    },
    sizeAttr() {
      this.updatePoints();
    },
    color() {
      this.updatePoints();
    },
    mode() {
      this.resetChart();
      this.updateBrush();
    },
    selection() {
      this.updateSelecteds();
      // this.updatePoints()
    },
    size() {
      this.resetChart();
    },
    highlightedSubject() {
      // this.highlightedData = this.highlightedSubject
      this.updateSelecteds();
    },
  },
};
</script>

<style lang="stylus" scoped>
.trials-scatter {
  position: relative;
  user-select: none;
  overflow: hidden;

  .overflow-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: auto;
  }

  .legend {
    background-color: white
    opacity: 0.75
    width: 120px;
    height: 45px;
    position: absolute;
    right: 0px;
    margin: 12px;
    border: 3px solid #FFF;
    display: flex
    flex-flow: row
  }

  .items {
    m-font-size(14,16)
    flex: 50%
    height: 45px
    line-height: 40px
  }


  .tooltip-div {
     text-align: center;
     padding: .5rem;
     background: white;
     color: #313639;
     border: 1px solid #313639;
     border-radius: 8px;
     pointer-events: none;
     font-size: 1.3rem;
  }
  >>> svg {
    user-select: none;

    .axis {

      .domain {
        stroke: #fff;
        stroke-width: 1px;
      }
    }

    .brush {
      .extent, .selection {
        fill: transparent;
        stroke: #444;
        stroke-dasharray: 1 3;
      }

      .hole-overlay {
        fill: rgba(0, 0, 20, 0.1);
        pointer-events: none;
      }
    }

    .content {

      .point {
        cursor: pointer;
        stroke-width: 0;
        transition: opacity 0.2s;

        &.selected {
          opacity: 1;
          // stroke-width 2px
          // stroke #0ff
          // .dot
          // stroke-width: 3px
        }

        &.unselected {
          opacity: 0.1;
        }

        &.highlighted {
          opacity: 1;
          stroke-width: 1px;
          stroke: #888;

          .dot {
            stroke-width: 0px;
          }
        }

        &.unhighlighted {
          // fill: #888
          opacity: 0.1;
        }
      }

      .spider {
        .leg {
          pointer-events: none;
          stroke: rgba(0, 0, 0, 0.15);
          stroke-width: 2px;
        }
      }
    }
  }

  .regression-tooltip {
    margin: 10px
  }

  .confidence-area {
    opacity: 0.4
    color: black
  }

  .brush-type-button {
    background: #fafafa;
    border-radius: 40px;
    box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.1);
    height: 40px;
    margin: 10px;
    position: absolute;
    right: 0;
    top: 0;
    width: 40px;

    &.poly {
      top: 50px;
    }

    &.selected {
      border: solid 1px #000;
    }
  }

  .brush-notice {
    position: absolute;
    top: 10px;
    left: 50%;
    color: $primary-btn-color;
    padding: 5px 10px;
    background: #fff;
    opacity: 0.6;
  }

  .zoom-reset {
    position: absolute;
    top: 10px;
    right: 10px;
    color: $primary-btn-color;
    padding: 5px 10px;
    background: #fff;
    opacity: 0.8;
    cursor: pointer;
  }

  .tippy-layer {
    pointer-events: none;
    position: absolute;
    width: 100% !important;
    height: 100% !important;
    top: 0;
    left: 0;
    border: none;

    >>>.tippy-box {
      pointer-events: all;

      .header {
        cursor: move;
      }
    }

    &.dragged {
      >>>.tippy-arrow {
        display: none;
      }
    }
  }
}
</style>